import React from 'react';
import classNames from 'classnames';
import { Connect, PropsFromRedux } from './SearchResultLegislationsContainer';
import {
  withRestrictedModal,
  WithRestrictedModalProps,
} from '../../../../components/modal/components/modal-restricted/withRestrictedModal';
import { Hyperlink } from '../../../../components/Hyperlink';
import {
  buildUrlWithParameters,
  getParametersFromUrl,
} from '../../../../utils/url-utils/urlUtils';
import { HitCommentariesList } from '../hit-commentaries/HitCommentariesList';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import { ItemSeparator } from '../../../../components/item-separator/ItemSeparator';
import { convertDateLawVersion } from '../../search-snippets/main/legislation/legislationSnippetUtils';
import { getFormattedDate } from '../../../../utils/date-utils/dateUtils';
import { withUser } from '../../../../user/UserContext';
import { UserContextType } from '../../../../user/user.types';
import { ScrollToMe } from '../../../../components/ScrollToMe';
import { HitSummary } from '../hit/summary/HitSummary';
import { getCommentariesLinkedToLegislation } from '../commentaries-modal/legislationApi';
import { SmallLineSeparator } from '../../../../components/separator/LineSeparator';
import { LegislationParts } from './LegislationParts';
import {
  ExcerptContainer,
  ExcerptCount,
} from '../../../../components/excerpt/Excerpt';
import { LegislationHit } from '../../../search.types';

type Props = PropsFromRedux & {
  legislations: LegislationHit[];
  searchQueryUrl: string;
} & UserContextType &
  WithRestrictedModalProps;

class RawSearchResultLegislations extends React.Component<Props> {
  position: number | null = null;

  constructor(props: Props) {
    super(props);
    const urlParams = getParametersFromUrl(window.location.search);
    this.position = urlParams.position
      ? parseInt(urlParams.position, 10)
      : null;
  }

  handleResultLawClicked = (legislation: LegislationHit, index: number) => (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    const { accessPremium, openRestrictedModal } = this.props;
    if (accessPremium) {
      doctrineAnalytics('Search ResultClicked', {
        type: 'law',
        position: index + 1,
        law_id: legislation.docId,
        url: legislation.url,
        reportOriginalQueryKey: true,
      });
    } else {
      event.preventDefault();
      openRestrictedModal('excerpt_results_law');
    }
  };

  render() {
    const { legislations, originalQueryKey, searchQueryUrl, type } = this.props;

    return (
      <>
        {legislations.map((legislation: LegislationHit, index: number) => {
          const searchParams = buildUrlWithParameters(legislation.url, {
            original_query_key: originalQueryKey,
            source: 'search_hit',
            position: index + 1,
          });
          const legislationUrl = `${searchParams}&${searchQueryUrl}`;
          return (
            <ScrollToMe
              key={legislation.docId}
              shouldScroll={this.position === index + 1}
            >
              {ref => (
                <ExcerptContainer ref={ref}>
                  <ItemSeparator />
                  <ExcerptCount>{index + 1}</ExcerptCount>
                  <Hyperlink
                    className={classNames('search-results-law__title', {
                      'search-results-law__title--visited': legislation.visited,
                    })}
                    href={legislationUrl}
                    onClick={this.handleResultLawClicked(legislation, index)}
                    dangerouslySetInnerHTML={{ __html: legislation.title }}
                  />
                  {!!legislation.startDate && (
                    <p>
                      {convertDateLawVersion({
                        start: legislation.startDate
                          ? getFormattedDate(legislation.startDate)
                          : null,
                        end: legislation.endDate
                          ? getFormattedDate(legislation.endDate)
                          : null,
                        state: legislation.state,
                        prefix: 'En vigueur ',
                      })}
                    </p>
                  )}
                  {legislation.parts.length > 0 ? (
                    <LegislationParts parts={legislation.parts} />
                  ) : (
                    <SmallLineSeparator />
                  )}
                  <HitSummary
                    hit={{
                      url: legislation.url,
                      visited: legislation.visited,
                      summary: legislation.preview,
                    }}
                    source="excerpt_results"
                    onHitClicked={this.handleResultLawClicked(
                      legislation,
                      index,
                    )}
                  />
                  <HitCommentariesList
                    commentaries={legislation.comments || []}
                    docId={legislation.docId}
                    numberOfComments={legislation.numberOfComments}
                    nextCommentAuthors={legislation.nextCommentAuthors}
                    otherCommentAuthorsCount={
                      legislation.otherCommentAuthorsCount
                    }
                    allCommentariesGetter={getCommentariesLinkedToLegislation}
                    source="legislation-results"
                    searchType={type}
                  />
                </ExcerptContainer>
              )}
            </ScrollToMe>
          );
        })}
      </>
    );
  }
}

export const SearchResultLegislations = withUser(
  withRestrictedModal(Connect(RawSearchResultLegislations)),
);
